import React, { useState } from "react";
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import { CSVLink } from "react-csv";
import jsPDF from "jspdf";
import "jspdf-autotable";
import autoTable from "jspdf-autotable";
import { Header } from "docx";
import { Heading } from "../../componenet/heading";
// Mock patients data
const mockPatients = [
  {
    id: "P1001",
    name: "Amit Srivastava",
    dob: "1985-06-15",
    age: 40,
    gender: "Male",
    blood_group: "B+",
    phone: "9876543210",
    opdHistory: [
      { visit_no: "OPD001", visit_date: "2025-08-30", department: "General Medicine", doctor: "Dr. A", reason: "Fever", status: "Completed" },
      { visit_no: "OPD002", visit_date: "2025-07-15", department: "Cardiology", doctor: "Dr. B", reason: "Chest Pain", status: "Completed" },
    ],
    ipdHistory: [
      { admission_no: "IPD001", admit_datetime: "2025-06-10 10:30", admission_reason: "Heart Surgery", current_bed_id: "B-101", doctor: "Dr. A", status: "Discharged" },
    ],
    radiologyHistory: [
      { order_datetime: "2025-08-15 09:00", source: "OPD", priority: "High", ordered_by: "Dr. B", status: "Completed" },
    ],
    pharmacyHistory: [
      { bill_no: "PH001", bill_date: "2025-08-20", total_amount: 500, paid_amount: 500, payment_mode: "Cash", status: "Paid" },
    ],
    billingHistory: [
      { invoice_no: "INV001", invoice_date: "2025-08-20", total_amount: 1500, net_amount: 1400, status: "Paid" },
    ],
  },
  {
    id: "P1002",
    name: "Amit Prakash Srivastava",
    dob: "1985-06-15",
    age: 40,
    gender: "Male",
    blood_group: "B+",
    phone: "9876543210",
    opdHistory: [
      { visit_no: "OPD003", visit_date: "2025-08-28", department: "Dermatology", doctor: "Dr. C", reason: "Skin Rash", status: "Completed" },
    ],
    ipdHistory: [],
    radiologyHistory: [],
    pharmacyHistory: [],
    billingHistory: [],
  },
];

const rowsPerPageOptions = [5, 10, 20];

const PatientHistory = () => {
  const [searchText, setSearchText] = useState("");
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [expanded, setExpanded] = useState(false);

  const [pageConfig, setPageConfig] = useState({
    opd: 0,
    ipd: 0,
    radiology: 0,
    pharmacy: 0,
    billing: 0,
  });
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [sortConfig, setSortConfig] = useState({
    opd: { key: "", direction: "asc" },
    ipd: { key: "", direction: "asc" },
    radiology: { key: "", direction: "asc" },
    pharmacy: { key: "", direction: "asc" },
    billing: { key: "", direction: "asc" },
  });

  // Search patients
  const handleSearch = () => {
    if (!searchText) return;
    const matches = mockPatients.filter(
      (p) =>
        p.name.toLowerCase().includes(searchText.toLowerCase()) ||
        p.id.toLowerCase().includes(searchText.toLowerCase())
    );
    setSearchResults(matches);
  };

  const selectPatient = (patient) => {
    setSelectedPatient(patient);
    setSearchResults([]);
    setExpanded(false);
    setPageConfig({ opd: 0, ipd: 0, radiology: 0, pharmacy: 0, billing: 0 });
  };

  const handleExpand = (panel) => (e, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const handlePageChange = (section) => (e, newPage) => {
    setPageConfig((prev) => ({ ...prev, [section]: newPage }));
  };

  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPageConfig({ opd: 0, ipd: 0, radiology: 0, pharmacy: 0, billing: 0 });
  };

  const handleSortClick = (section, col) => {
    const key = col.toLowerCase().replace(/ /g, "_");
    setSortConfig((prev) => {
      const direction =
        prev[section].key === key && prev[section].direction === "asc"
          ? "desc"
          : "asc";
      return { ...prev, [section]: { key, direction } };
    });
  };

  const getSortedData = (sectionData, section) => {
    const { key, direction } = sortConfig[section];
    if (!key) return sectionData;
    return [...sectionData].sort((a, b) => {
      if (a[key] < b[key]) return direction === "asc" ? -1 : 1;
      if (a[key] > b[key]) return direction === "asc" ? 1 : -1;
      return 0;
    });
  };

  const exportPDF = (sectionName, data, columns) => {
    const doc = new jsPDF();
    doc.text(`${selectedPatient.name} - ${sectionName}`, 14, 15);
    const tableData = data.map((row) =>
      columns.map((col) => row[col.toLowerCase().replace(/ /g, "_")])
    );
   // doc.autoTable({ head: [columns], body: tableData, startY: 20 });
   autoTable(doc, { head: [columns], body: tableData, startY: 20 });
    doc.save(`${selectedPatient.name}_${sectionName}.pdf`);
  };

  const exportPrint = (sectionName, data, columns) => {
    let html = `<h3>${selectedPatient.name} - ${sectionName}</h3><table border="1"><thead><tr>`;
    columns.forEach((col) => (html += `<th>${col}</th>`));
    html += "</tr></thead><tbody>";
    data.forEach((row) => {
      html += "<tr>";
      columns.forEach((col) => {
        html += `<td>${row[col.toLowerCase().replace(/ /g, "_")]}</td>`;
      });
      html += "</tr>";
    });
    html += "</tbody></table>";
    const w = window.open();
    w.document.write(html);
    w.document.close();
    w.print();
  };

  // Sections definition
  const sections = selectedPatient
    ? [
        { name: "OPD", data: selectedPatient.opdHistory, columns: ["Visit No", "Visit Date", "Department", "Doctor", "Reason", "Status"], key: "opd" },
        { name: "IPD", data: selectedPatient.ipdHistory, columns: ["Admission No", "Admit Datetime", "Admission Reason", "Current Bed Id", "Doctor", "Status"], key: "ipd" },
        { name: "Radiology", data: selectedPatient.radiologyHistory, columns: ["Order Datetime", "Source", "Priority", "Ordered By", "Status"], key: "radiology" },
        { name: "Pharmacy", data: selectedPatient.pharmacyHistory, columns: ["Bill No", "Bill Date", "Total Amount", "Paid Amount", "Payment Mode", "Status"], key: "pharmacy" },
        { name: "Billing", data: selectedPatient.billingHistory, columns: ["Invoice No", "Invoice Date", "Total Amount", "Net Amount", "Status"], key: "billing" },
      ]
    : [];

  const fullHistoryCSV = sections.flatMap((sec) =>
    sec.data.map((row) => ({ ...row, section: sec.name }))
  );

  return (
    <Box sx={{ p: 3,mt:3 }}>

       <Heading headingtext="🕘 Patient History" />
      {/* Search */}
      <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
        <TextField
          label="Search by Name or ID"
          variant="outlined"
          size="small"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          sx={{ flex: 1 }}
        />
        <Button
          variant="contained"
          startIcon={<SearchIcon />}
          onClick={handleSearch}
        >
          Search
        </Button>
      </Box>

      {/* Search Results */}
      {searchResults.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1">Select a Patient:</Typography>
          {searchResults.map((p) => (
            <Box
              key={p.id}
              sx={{
                p: 1,
                mb: 1,
                border: "1px solid #ccc",
                borderRadius: 1,
                cursor: "pointer",
                "&:hover": { backgroundColor: "#f0f0f0" },
              }}
              onClick={() => selectPatient(p)}
            >
              {p.name} ({p.id})
            </Box>
          ))}
        </Box>
      )}

      {/* Full History */}
      {selectedPatient && (
        <Box>
          <Typography variant="h5" gutterBottom>
            {selectedPatient.name} - Full Patient History
          </Typography>

          {/* Full History Export Buttons */}
          <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
            <CSVLink data={fullHistoryCSV} filename={`${selectedPatient.name}_FullHistory.csv`}>
              <Button variant="outlined">Export CSV</Button>
            </CSVLink>
            <Button
              variant="outlined"
              onClick={() => {
                const doc = new jsPDF();
                doc.text(`${selectedPatient.name} - Full History`, 14, 15);
                let y = 20;
                sections.forEach((sec) => {
                  doc.text(sec.name, 14, y);
                  y += 5;
                  const tableData = sec.data.map((row) =>
                    sec.columns.map((col) => row[col.toLowerCase().replace(/ /g, "_")])
                  );
                 // doc.autoTable({ head: [sec.columns], body: tableData, startY: y });
                 autoTable(doc, { head: [sec.columns], body: tableData, startY: 20 });
                  y += tableData.length * 8 + 10;
                });
                doc.save(`${selectedPatient.name}_FullHistory.pdf`);
              }}
            >
              Export PDF
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                let html = `<h3>${selectedPatient.name} - Full History</h3>`;
                sections.forEach((sec) => {
                  html += `<h4>${sec.name}</h4><table border="1"><thead><tr>`;
                  sec.columns.forEach((col) => (html += `<th>${col}</th>`));
                  html += "</tr></thead><tbody>";
                  sec.data.forEach((row) => {
                    html += "<tr>";
                    sec.columns.forEach((col) => {
                      html += `<td>${row[col.toLowerCase().replace(/ /g, "_")]}</td>`;
                    });
                    html += "</tr>";
                  });
                  html += "</tbody></table><br/>";
                });
                const w = window.open();
                w.document.write(html);
                w.document.close();
                w.print();
              }}
            >
              Print Full History
            </Button>
          </Box>

          {/* Accordion Sections */}
          {sections.map((sec) => {
            const sortedData = getSortedData(sec.data, sec.key);
            const paginatedData = sortedData.slice(
              pageConfig[sec.key] * rowsPerPage,
              pageConfig[sec.key] * rowsPerPage + rowsPerPage
            );

            return (
              <Accordion
                key={sec.key}
                expanded={expanded === sec.key}
                onChange={handleExpand(sec.key)}
                sx={{ mb: 2 }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography sx={{ flex: 1, fontWeight: "bold" }}>
                    {sec.name} ({sec.data.length})
                  </Typography>
                </AccordionSummary>

                <AccordionDetails>
                  <Box sx={{ display: "flex", gap: 2, mb: 1, flexWrap: "wrap" }}>
                    <CSVLink data={sec.data} filename={`${selectedPatient.name}_${sec.name}.csv`}>
                      <Button variant="outlined" size="small">CSV</Button>
                    </CSVLink>
                    <Button variant="outlined" size="small" onClick={() => exportPDF(sec.name, sec.data, sec.columns)}>PDF</Button>
                    <Button variant="outlined" size="small" onClick={() => exportPrint(sec.name, sec.data, sec.columns)}>Print</Button>
                  </Box>

                  <Box sx={{ overflowX: "auto" }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          {sec.columns.map((col) => {
                            const key = col.toLowerCase().replace(/ /g, "_");
                            return (
                              <TableCell
                                key={key}
                                sx={{ cursor: "pointer" }}
                                onClick={() => handleSortClick(sec.key, col)}
                              >
                                {col} {sortConfig[sec.key].key === key ? (sortConfig[sec.key].direction === "asc" ? "▲" : "▼") : ""}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {paginatedData.map((row, idx) => (
                          <TableRow key={idx}>
                            {sec.columns.map((col) => {
                              const key = col.toLowerCase().replace(/ /g, "_");
                              return <TableCell key={key}>{row[key]}</TableCell>;
                            })}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>

                  <TablePagination
                    component="div"
                    count={sec.data.length}
                    page={pageConfig[sec.key]}
                    onPageChange={handlePageChange(sec.key)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleRowsPerPageChange}
                    rowsPerPageOptions={rowsPerPageOptions}
                  />
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default PatientHistory;
